import { annualQuestionPercent } from './evaluation-scoring.utils';

/** Construye el mapa de metas personales por pregunta. */
export function buildGoalMap(goals: any[]): Record<string, any> {
  const map: Record<string, any> = {};
  for (const goal of (goals || [])) map[goal.questionId] = goal;
  return map;
}

/** Construye el mapa de valores objetivo por pregunta. */
export function buildGoalNumbers(goals: any[]): Record<string, number> {
  const map: Record<string, number> = {};
  for (const goal of (goals || [])) map[goal.questionId] = Number(goal.targetValue);
  return map;
}

/** Recolecta ids de preguntas personales sin meta definida. */
export function collectMissingIds(evaluation: any[], map: Record<string, any>): Set<string> {
  const ids = new Set<string>();
  for (const cat of (evaluation || [])) collectMissingFromCategory(cat, ids, map);
  return ids;
}

/** Revisa las respuestas de una categoría buscando metas faltantes. */
function collectMissingFromCategory(cat: any, ids: Set<string>, map: Record<string, any>): void {
  for (const ans of (cat.answers || [])) collectAnswerMissing(ans, ids, map);
}

/** Revisa una respuesta y sus subitems buscando metas faltantes. */
function collectAnswerMissing(ans: any, ids: Set<string>, map: Record<string, any>): void {
  checkAnswerMissing(ans, ids, map);
  collectSubsMissing(ans?.subitems, ids, map);
}

/** Revisa los subitems buscando metas personales faltantes. */
function collectSubsMissing(subs: any[], ids: Set<string>, map: Record<string, any>): void {
  for (const sub of (subs || [])) checkAnswerMissing(sub, ids, map);
}

/** Marca la pregunta si es personal y no tiene meta definida. */
function checkAnswerMissing(ans: any, ids: Set<string>, map: Record<string, any>): void {
  if (isPersonalWithoutGoal(ans, map)) ids.add(ans.question.id);
}

/** Indica si la respuesta es personal y carece de meta. */
function isPersonalWithoutGoal(ans: any, map: Record<string, any>): boolean {
  return ans?.question?.goalType === 'personal' && !map[ans.question.id];
}

/** Recolecta ids de preguntas cuya meta anual ya se cumplió. */
export function collectCompletedIds(
  evaluation: any[],
  base: Record<string, number>,
  goals: Record<string, number>,
): Set<string> {
  const ids = new Set<string>();
  for (const cat of (evaluation || [])) collectCategoryCompleted(cat, ids, base, goals);
  return ids;
}

/** Revisa las respuestas de una categoría buscando metas cumplidas. */
function collectCategoryCompleted(
  cat: any,
  ids: Set<string>,
  base: Record<string, number>,
  goals: Record<string, number>,
): void {
  for (const ans of (cat.answers || [])) collectItemCompleted(ans, ids, base, goals);
}

/** Revisa un item y sus subitems buscando metas cumplidas. */
function collectItemCompleted(
  item: any,
  ids: Set<string>,
  base: Record<string, number>,
  goals: Record<string, number>,
): void {
  addIfComplete(item, ids, base, goals);
  collectSubsCompleted(item?.subitems, ids, base, goals);
}

/** Revisa los subitems buscando metas anuales cumplidas. */
function collectSubsCompleted(
  subs: any[],
  ids: Set<string>,
  base: Record<string, number>,
  goals: Record<string, number>,
): void {
  for (const sub of (subs || [])) addIfComplete(sub, ids, base, goals);
}

/** Marca la pregunta si su acumulado alcanza la meta anual. */
function addIfComplete(
  target: any,
  ids: Set<string>,
  base: Record<string, number>,
  goals: Record<string, number>,
): void {
  if (reachesGoalFor(target, base, goals)) ids.add(target.question.id);
}

/** Indica si el acumulado del objetivo alcanza su meta. */
function reachesGoalFor(target: any, base: Record<string, number>, goals: Record<string, number>): boolean {
  const question = target?.question;
  if (!hasGoalConfig(question)) return false;
  return reachesAnnual(question, base, goals);
}

/** Indica si el porcentaje anual del objetivo llega a 100. */
function reachesAnnual(q: any, base: Record<string, number>, goals: Record<string, number>): boolean {
  return annualQuestionPercent(q, cumulOf(base, q.id), goals || {}) >= 100;
}

/** Obtiene el acumulado base de una pregunta o cero. */
function cumulOf(base: Record<string, number>, qId: string): number {
  if (!base) return 0;
  return base[qId] || 0;
}

/** Aplica la base acumulada del año a la fecha. */
export function applyYtdToState(state: any, res: any): void {
  state.ytdBase = res?.baseCumul || {};
  state.ytdAnswerBase = res?.baseAnswerCumul || {};
}

/** Vacía los conjuntos de metas anuales. */
export function clearGoalSets(state: any): void {
  state.missingGoalIds = new Set();
  state.completedGoalIds = new Set();
}

/** Limpia los acumulados del año a la fecha. */
export function clearYtdState(state: any): void {
  state.ytdBase = {};
  state.ytdAnswerBase = {};
  state.completedGoalIds = new Set();
}

/** Aplica las metas y recalcula las faltantes. */
export function applyGoalsToState(state: any, goals: any[]): void {
  const map = buildGoalMap(goals);
  state.missingGoalIds = collectMissingIds(state.evaluation, map);
  state.goalNumbers = buildGoalNumbers(goals);
}

/** Indica si la pregunta tiene configuración de meta. */
function hasGoalConfig(q: any): boolean {
  return q?.goalType === 'general' || q?.goalType === 'personal';
}
